// Date-window math for the team statistics period picker.

/**
 * The Nordic hunting year runs from July 1 through June 30 of the following
 * calendar year. These name the boundary months (1-indexed, so July = 7 and
 * June = 6) rather than leaving the raw month numbers scattered through the
 * window math below — `getMonth()` is 0-indexed, hence the `- 1` at each use.
 */
export const HUNTING_YEAR_START_MONTH = 7 // July
export const HUNTING_YEAR_END_MONTH = 6 // June

/** The span of the "Last 30 days" preset, in days. */
export const LAST_N_DAYS = 30

const MS_PER_DAY = 24 * 60 * 60 * 1000

export interface DateWindow {
  from: Date
  to: Date
}

/**
 * The hunting-year window (July 1 → June 30) that CONTAINS `now`. A date on or
 * after July 1 belongs to the year that starts that July; a date in January–June
 * belongs to the year that started the PREVIOUS July and ends this June 30. The
 * window covers the whole first and last day (00:00:00.000 → 23:59:59.999) so an
 * inclusive backend range picks up reports made at any time on the boundary days.
 */
export function huntingYearWindow(now: Date): DateWindow {
  const year = now.getFullYear()
  // getMonth() is 0-indexed, so July (start month 7) is index 6.
  const inNewHuntingYear = now.getMonth() >= HUNTING_YEAR_START_MONTH - 1
  const startYear = inNewHuntingYear ? year : year - 1
  const from = new Date(startYear, HUNTING_YEAR_START_MONTH - 1, 1, 0, 0, 0, 0)
  const to = new Date(startYear + 1, HUNTING_YEAR_END_MONTH - 1, 30, 23, 59, 59, 999)
  return { from, to }
}

/** The trailing `LAST_N_DAYS`-day window ending exactly at `now`. */
export function lastNDaysWindow(now: Date): DateWindow {
  const from = new Date(now.getTime() - LAST_N_DAYS * MS_PER_DAY)
  return { from, to: new Date(now.getTime()) }
}
